/**
 * Dedicated landing page for "Beyond Simple Aligners: Biomechanics and
 * Planning for Severe Malocclusions" by Dr. Diego Peydro.
 *
 * Design: inspired by the agency-supplied landing-page HTML, rebuilt with
 * the Albanian Aligners Academy brand palette (#003F72 deep blue + #1CA3B0
 * turquoise + #3CD4E1 accent) instead of the original gold.
 *
 * All CTAs ("Regjistrohu tani", "Paguaj me Kartë") open the shared
 * `<PaidRegistrationPopup>` component so this page benefits from the full
 * existing checkout flow (POK, voucher, badge upload, invoice emails).
 *
 * This page is served OUTSIDE the main <Layout>, so no header/footer from
 * the rest of the site appears — it is a pure conversion-focused page.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import axios from "axios";
import { PaidRegistrationPopup } from "./EventDetailPage";
import { LanguageSelector } from "../components/Layout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const BRAND = {
  deep: "#003F72",
  turq: "#1CA3B0",
  accent: "#3CD4E1",
  dark: "#0A2A47",
  cream: "#F4F7F9",
  textLight: "#CFE0EC",
  textMuted: "#8FB3CD",
};

const EVENT_SLUG_FRAGMENT = "beyond-simple-aligners-biomechanics-and-planning";

// tx-catalog-start
const marqueeKeywords = [
  "Open bite",
  "Distalizim",
  "Protokolle COIP",
  "Raste klinike",
  "Biomekanikë",
  "Planifikim digjital",
  "Klasë II",
];

const learningPoints = [
  { title: "Diagnozë & Planifikim i Avancuar", text: "Si të vlerësoni rastet komplekse me kritere të qarta klinikë, radiologjikë dhe biomekanikë përpara se të filloni çdo lëvizje." },
  { title: "Kontroll Vertikal / Sagital / Tërthor", text: "Teknikat që realisht funksionojnë për të kontrolluar overjet, overbite dhe gjerësinë në rastet më kërkuese." },
  { title: "Strategji pa Ekstraksione dhe pa Kirurgji", text: "Kur dhe si të shmangni ekstraksionet dhe ortognatikën kur aligner-at mund ta zgjidhin rastin klinikisht." },
  { title: "Protokolle Praktike COIP", text: "Protokolli hap-pas-hapi i zhvilluar nga Dr. Peydro & Dr. Iván Malagón, i gatshëm për t'u aplikuar në klinikën tuaj të nesërmen." },
  { title: "Finalizim & Stabilitet Afatgjatë", text: "Si të finalizoni rastin që rezultati të qëndrojë vite më vonë — jo thjesht në fund të serisë së aligner-ave." },
];

const agendaModules = [
  { num: "01", title: "Diagnoza e Rasteve të Komplikuara", points: [
    "Vlerësimi klinikë dhe radiologjikë",
    "Identifikimi i kufijve biomekanikë",
    "Komunikimi i planit me pacientin",
  ]},
  { num: "02", title: "Biomekanika e Aligner-ave në Thellësi", points: [
    "Forcat, vektorët dhe kontrolli i rrënjës",
    "Attachments: kur, ku, pse",
    "Mikrokorrigjimet gjatë serisë",
  ]},
  { num: "03", title: "Klasa II & Klasa III", points: [
    "Strategjitë e distalizimit dhe mezializimit",
    "Elementet ndihmëse (elastics, miniscrews)",
    "Rastet kufitare: aligner apo kirurgji?",
  ]},
  { num: "04", title: "Kontrolli Vertikal — Deep Bite & Open Bite", points: [
    "Intrusionet e mirë planifikuara",
    "Ekstrusionet e kontrolluara në sektorin posterior",
    "Shmangia e relapse-s",
  ]},
  { num: "05", title: "Kontrolli Transversal", points: [
    "Zgjerime të parashikueshme",
    "Kur duhet MARPE / SARPE",
    "Stabilitet afatgjatë transversal",
  ]},
  { num: "06", title: "Ekzekutimi Klinik — Rast pas Rasti", points: [
    "Prezantim rastesh reale me vijueshmëri",
    "Vendimet në çdo kontroll",
    "Komunikimi me laboratorin dhe digital-workflow",
  ]},
  { num: "07", title: "Finalizimi & Retencioni", points: [
    "Protokollet e finalizimit mikro-metrik",
    "Zgjedhja e retainer-it të duhur",
    "Follow-up afatgjatë dhe menaxhimi i rikthimit",
  ]},
];

const faqItems = [
  { q: "Për kë është ky Masterclass?", a: "Për dentistë dhe ortodontë që tashmë kanë eksperiencë me aligner-at dhe duan t'i përballojnë rastet më komplekse me siguri." },
  { q: "Në çfarë gjuhe zhvillohet?", a: "Anglisht me përkthim profesional në shqip në kohë reale." },
  { q: "A merr certifikatë?", a: "Po, çdo pjesëmarrës merr Certifikatë Pjesëmarrjeje nga Albanian Aligners Academy." },
  { q: "A mund ta transferoj biletën?", a: "Po, transfero biletën te një koleg deri 7 ditë para eventit duke na kontaktuar." },
  { q: "Çfarë përfshin bileta VIP?", a: "Përveç gjithçkaje që ka Standard, VIP përfshin hyrjen në eventin e dedikuar pas masterclass në mbrëmje me liderë të ortodoncisë, mundësi për diskutim të rasteve klinike dhe mundësi rimbursimi 100% të biletës nëse bëni 3 porosi në iClear Aligners." },
];

/**
 * The 3 ticket tiers shown on the landing page.
 * These labels must match (case-insensitive) the `pricing_tiers[].label` for
 * this event in MongoDB so the backend `/api/checkout/pok/create` endpoint
 * resolves the correct price. Prices here are only for display; the backend
 * is always the source of truth for the amount charged.
 */
const LANDING_TIERS = [
  {
    label: "Standard",
    price: 350,
    features: [
      "Qasje në të gjitha sesionet e ditës",
      "Materialet e kursit",
      "Kafe dhe drekë",
      "Certifikatë e pjesëmarrjes",
    ],
  },
  {
    label: "VIP",
    price: 450,
    features: [
      "Të gjitha përfitimet Standard",
      "Hyrje në eventin e dedikuar pas masterclass në mbrëmje me liderë të ortodoncisë",
      "Mundësi për diskutime të rasteve klinike",
      "Mundësi për rimbursim të 100% të biletës nëse do të bëni 3 porosi në iClear Aligners (kompani pjesë e grupit tonë)",
    ],
  },
  {
    label: "Group Ticket",
    price: 900,
    features: [
      "Qasje në të gjitha sesionet e ditës për 3 persona",
      "Materialet e kursit",
      "Kafe dhe drekë",
      "Pozicionim në sallë pranë njëri-tjetrit",
    ],
  },
];
// tx-catalog-end

const BeyondSimpleAlignersLanding = () => {
  const { tx } = useLanguage();
  const [event, setEvent] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [openPopup, setOpenPopup] = useState(null); // selectedTier object or null
  const [openModuleIdx, setOpenModuleIdx] = useState(0);
  const [openFaqIdx, setOpenFaqIdx] = useState(null);

  useEffect(() => {
    document.title = "Beyond Simple Aligners — Dr. Diego Peydro | Albanian Aligners Academy";
    (async () => {
      try {
        const [eRes, pmRes] = await Promise.all([
          axios.get(`${API}/events`),
          axios.get(`${API}/payment-methods/public`).catch(() => ({ data: [] })),
        ]);
        const match = (eRes.data || []).find((e) =>
          (e.slug || "").toLowerCase().includes(EVENT_SLUG_FRAGMENT)
        );
        setEvent(match || null);
        setPaymentMethods(pmRes.data?.methods || []);
      } catch (err) {
        console.error(err);
      }
    })();
  }, []);

  const tiers = useMemo(() => LANDING_TIERS, []);
  // Match a hardcoded tier to the real DB tier so the backend validates the
  // amount correctly. If the DB has a tier with the same label we send that
  // one; otherwise we send the hardcoded fallback (backend may 400 — the
  // admin needs to keep DB tiers in sync).
  const resolveTier = (hc) => {
    const dbTiers = event?.pricing_tiers || [];
    const match = dbTiers.find((t) => (t.label || "").toLowerCase() === hc.label.toLowerCase());
    return match || hc;
  };
  const openRegister = (tier) => setOpenPopup({ tier: resolveTier(tier) });

  // Deep links: #standard-regjistrohu, #vip-regjistrohu, #group-regjistrohu open the popup directly
  useEffect(() => {
    if (!event) return undefined;
    const openFromHash = () => {
      const m = window.location.hash.toLowerCase().match(/^#([a-z]+)-regjistrohu$/);
      const tier = m && tiers.find((t) => t.label.toLowerCase().split(" ")[0] === m[1]);
      if (tier) {
        scrollToTickets();
        openRegister(tier);
      }
    };
    openFromHash();
    window.addEventListener("hashchange", openFromHash);
    return () => window.removeEventListener("hashchange", openFromHash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);

  const closePopup = () => {
    setOpenPopup(null);
    if (/-regjistrohu$/i.test(window.location.hash)) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
  };

  const scrollToTickets = () => document.getElementById("biletat")?.scrollIntoView({ block: "start" });

  // Smooth-scroll helper for in-page nav
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="font-['Manrope','Helvetica_Neue',Arial,sans-serif] text-[#0A2A47] bg-white" data-testid="beyond-landing">
      {/* NAV */}
      <nav className="sticky top-0 z-40 backdrop-blur-md bg-white/85 border-b border-slate-200">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-3">
          <a href="/" className="flex items-center gap-2">
            <img src={`${BACKEND_URL}/api/files/local/logo-normal.png`} alt="Albanian Aligners Academy" className="h-8 sm:h-10 w-auto" />
          </a>
          <div className="hidden md:flex items-center gap-6 text-sm font-semibold text-[#4A5568]">
            <button onClick={() => scrollTo("eventi")} className="hover:text-[#1CA3B0] transition-colors">{tx("Eventi")}</button>
            <button onClick={() => scrollTo("lektori")} className="hover:text-[#1CA3B0] transition-colors">{tx("Lektori")}</button>
            <button onClick={() => scrollTo("programi")} className="hover:text-[#1CA3B0] transition-colors">{tx("Programi")}</button>
            <button onClick={() => scrollTo("biletat")} className="hover:text-[#1CA3B0] transition-colors">{tx("Biletat")}</button>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
          <div data-testid="landing-language-selector"><LanguageSelector /></div>
          <button
            onClick={() => openRegister(tiers.find((t) => t.label === "Standard") || tiers[0])}
            className="whitespace-nowrap rounded-full px-4 sm:px-5 py-2 text-sm font-bold text-white hover:-translate-y-0.5 transition-all shadow-md"
            style={{ background: `linear-gradient(130deg, ${BRAND.accent} 0%, ${BRAND.deep} 100%)` }}
            data-testid="nav-register"
          >
            {tx("Regjistrohu")}
          </button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative overflow-hidden text-white" style={{ background: `linear-gradient(135deg, ${BRAND.deep} 0%, ${BRAND.dark} 60%, #042E52 100%)` }}>
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
          backgroundImage: "radial-gradient(circle at 20% 30%, rgba(60,212,225,0.35) 0%, transparent 55%), radial-gradient(circle at 80% 70%, rgba(28,163,176,0.35) 0%, transparent 55%)",
        }} />
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28">
          <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-bold tracking-wider uppercase mb-6 text-white" style={{ background: `${BRAND.accent}22`, border: `1px solid ${BRAND.accent}55` }}>
            <span className="w-2 h-2 rounded-full" style={{ background: BRAND.accent }} /> {tx("Masterclass · 27 Nëntor 2026")}
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-black leading-[1.05] tracking-tight max-w-5xl text-white" style={{ textWrap: "balance" }}>
            Beyond Simple Aligners
          </h1>
          <p className="mt-4 text-xl md:text-2xl font-semibold max-w-4xl text-white/90">
            Biomechanics and Planning for Severe Malocclusions
          </p>
          <p className="mt-6 text-base md:text-lg max-w-3xl leading-relaxed text-white/70">
            {tx("Një mësim i plotë praktik me")} <b className="text-white">{tx("Dr. Diego Peydro")}</b> {tx("për trajtimin e malocluzioneve komplekse me aligners — i fokusuar te parashikueshmëria, efikasiteti dhe shmangia e ekstraksioneve ose kirurgjisë ortognatike kur është klinikisht e përshtatshme.")}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => openRegister(tiers.find((t) => t.label === "Standard") || tiers[0])}
              className="rounded-full px-8 py-4 font-bold text-white shadow-xl hover:-translate-y-0.5 transition-all inline-flex items-center justify-center gap-2"
              style={{ background: `linear-gradient(130deg, ${BRAND.accent} 0%, ${BRAND.deep} 100%)`, boxShadow: `0 20px 40px -10px ${BRAND.accent}66` }}
              data-testid="hero-register"
            >
              {tx("Regjistrohu tani")} <i className="fas fa-arrow-right" />
            </button>
            <button
              onClick={() => scrollTo("programi")}
              className="rounded-full px-8 py-4 font-bold border-2 border-white/30 text-white hover:border-white hover:bg-white/10 transition-all"
              data-testid="hero-program"
            >
              {tx("Shiko programin")}
            </button>
          </div>

          {/* Meta strip */}
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: "fa-calendar", label: tx("Data"), value: tx("27 Nëntor 2026") },
              { icon: "fa-clock", label: tx("Ora"), value: "09:00 — 17:30" },
              { icon: "fa-location-dot", label: tx("Vendi"), value: event?.location || tx("Tiranë") },
              { icon: "fa-language", label: tx("Gjuha"), value: tx("EN me përkth. SQ") },
            ].map((m) => (
              <div key={m.label} className="rounded-xl border border-white/10 bg-white/5 backdrop-blur px-4 py-3">
                <div className="text-xs uppercase tracking-wider" style={{ color: BRAND.textMuted }}><i className={`fas ${m.icon} mr-1.5`} /> {m.label}</div>
                <div className="mt-1 text-sm font-bold text-white">{m.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="relative py-5 overflow-hidden" style={{ background: BRAND.accent }}>
        <div className="flex gap-10 animate-[marquee_30s_linear_infinite] whitespace-nowrap">
          {[...marqueeKeywords, ...marqueeKeywords, ...marqueeKeywords].map((kw, i) => (
            <span key={i} className="text-base md:text-lg font-black tracking-wider uppercase text-[#003F72]">{tx(kw)} <span className="opacity-50 mx-4">◆</span></span>
          ))}
        </div>
      </div>
      <style>{`@keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-33.33%); } }`}</style>

      {/* ABOUT */}
      <section id="eventi" className="max-w-7xl mx-auto px-6 py-20 md:py-28">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <div className="lg:self-center" data-testid="about-event-heading">
            <p className="flex items-center gap-3 text-xs font-black uppercase tracking-widest" style={{ color: BRAND.turq }}>
              <span className="inline-block h-[2px] w-10 rounded-full" style={{ background: BRAND.deep }} aria-hidden="true" />
              {tx("Mbi eventin")}
            </p>
            <h2 className="mt-3 text-3xl md:text-5xl font-black leading-tight" style={{ color: BRAND.deep, textWrap: "balance" }}>
              {tx("Alignerat kanë evoluar.")}<br/>{tx("Po protokollet tuaja?")}
            </h2>
          </div>
          <div className="space-y-5 text-base md:text-lg leading-relaxed text-[#4A5568]">
            <p>{tx("Terapia me alignera është zgjeruar shumë përtej rasteve të lehta. Malokluzionet e rënda — mbivendosjet e theksuara, open bite anterior, Klasë II & III me nevojë distalizimi — kërkojnë kuptim të plotë të biomekanikës dhe një plan digjital të ndërtuar hap pas hapi.")}</p>
            <p>{tx("Dr. Diego Peydro do të demonstrojë raste komplekse reale bazuar në ligje biomekanike të provuara klinikisht. Pa teori të përgjithësuar: vendimmarrje e organizuar që mund ta filloni ta aplikoni në klinikën tuaj menjëherë.")}</p>
            <div className="grid grid-cols-3 gap-4 mt-8">
              {[
                { n: tx("8 orë"), l: tx("Trajnim intensiv, një ditë e plotë") },
                { n: "15+", l: tx("Vite eksperiencë në trajnimin e klinicistëve") },
                { n: "1000+", l: tx("Dentistë të trajnuar me protokollet COIP") },
              ].map((s) => (
                <div key={s.l} className="rounded-xl p-4 text-center" style={{ background: BRAND.cream }}>
                  <div className="text-2xl md:text-3xl font-black" style={{ color: BRAND.turq }}>{s.n}</div>
                  <div className="text-xs md:text-sm text-[#4A5568] mt-1 font-semibold">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHAT YOU'LL LEARN */}
      <section className="py-20 md:py-28" style={{ background: BRAND.cream }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-xs font-black uppercase tracking-widest" style={{ color: BRAND.turq }}>{tx("Çfarë do të mësoni")}</p>
            <h2 className="mt-3 text-3xl md:text-5xl font-black leading-tight" style={{ color: BRAND.deep, textWrap: "balance" }}>
              {tx("5 shtylla që ndryshojnë mënyrën si trajtoni rastet komplekse")}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {learningPoints.map((p, i) => (
              <div key={i} className="rounded-2xl bg-white p-7 shadow-sm hover:shadow-lg transition-shadow border border-slate-100">
                <div className="flex items-start gap-5">
                  <div className="shrink-0 w-14 h-14 rounded-xl flex items-center justify-center text-white text-2xl font-black" style={{ background: `linear-gradient(130deg, ${BRAND.accent} 0%, ${BRAND.deep} 100%)` }}>
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div>
                    <h3 className="text-xl font-black" style={{ color: BRAND.deep }}>{tx(p.title)}</h3>
                    <p className="mt-2 text-sm md:text-base text-[#4A5568] leading-relaxed">{tx(p.text)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SPEAKER */}
      <section id="lektori" className="max-w-7xl mx-auto px-6 py-20 md:py-28">
        <div className="grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-2">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5] shadow-2xl" style={{ background: `linear-gradient(135deg, ${BRAND.deep} 0%, ${BRAND.dark} 100%)` }}>
              <img src={`${BACKEND_URL}/api/files/local/trainer-diego-peydro.jpg`} alt="Dr. Diego Peydro" className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              <div className="absolute inset-0 bg-gradient-to-t from-[#003F72]/70 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="text-xs font-bold uppercase tracking-wider text-white/80">{tx("Lektori")}</p>
                <h3 className="text-2xl md:text-3xl font-black mt-1 text-white drop-shadow-lg">Dr. Diego Peydro</h3>
              </div>
            </div>
          </div>
          <div className="lg:col-span-3">
            <p className="text-xs font-black uppercase tracking-widest" style={{ color: BRAND.turq }}>{tx("Me kë mësoni")}</p>
            <h2 className="mt-3 text-3xl md:text-5xl font-black leading-tight" style={{ color: BRAND.deep }}>
              Dr. Diego Peydro
            </h2>
            <p className="mt-2 text-base md:text-lg font-semibold" style={{ color: BRAND.turq }}>
              Co-director, Peydro Dental Clinic · Master COIP · Valencia, Spain
            </p>
            <p className="mt-6 text-base md:text-lg text-[#4A5568] leading-relaxed">
              {tx("Dr. Peydro specializohet në menaxhimin e rasteve komplekse me aligners — malocluzionet më kërkuese, Klasat II dhe III, open bites dhe situatat kufitare që shumë kolegë i referojnë te kirurgu. Protokolli i tij praktik COIP, i zhvilluar së bashku me Dr. Iván Malagón, i mëson klinicistët të kenë rezultate të parashikueshme pa ekstraksione e pa kirurgji ortognatike kur klinika e lejon.")}
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Severe Class II", "Severe Class III", "Open Bite", "Deep Bite", "COIP Protocol", tx("Finalizim stabël")].map((tag) => (
                <span key={tag} className="rounded-full px-3 py-1 text-xs font-bold" style={{ background: `${BRAND.turq}15`, color: BRAND.deep, border: `1px solid ${BRAND.turq}40` }}>{tag}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* AGENDA */}
      <section id="programi" className="py-20 md:py-28" style={{ background: BRAND.cream }}>
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-xs font-black uppercase tracking-widest" style={{ color: BRAND.turq }}>{tx("Programi")}</p>
            <h2 className="mt-3 text-3xl md:text-5xl font-black leading-tight" style={{ color: BRAND.deep }}>
              {tx("7 module. Një ditë e plotë.")}<br/>{tx("Zero teori boshe.")}
            </h2>
          </div>
          <div className="space-y-3">
            {agendaModules.map((m, idx) => {
              const open = openModuleIdx === idx;
              return (
                <div key={m.num} className="rounded-2xl bg-white border border-slate-200 overflow-hidden shadow-sm">
                  <button
                    onClick={() => setOpenModuleIdx(open ? -1 : idx)}
                    className="w-full px-6 py-5 flex items-center gap-5 text-left hover:bg-slate-50 transition-colors"
                    data-testid={`agenda-${m.num}`}
                  >
                    <div className="shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-white font-black text-sm" style={{ background: `linear-gradient(130deg, ${BRAND.accent} 0%, ${BRAND.deep} 100%)` }}>
                      {m.num}
                    </div>
                    <h3 className="flex-1 text-base md:text-xl font-black" style={{ color: BRAND.deep }}>{tx(m.title)}</h3>
                    <i className={`fas fa-chevron-down text-sm transition-transform ${open ? "rotate-180" : ""}`} style={{ color: BRAND.turq }} />
                  </button>
                  {open && (
                    <div className="px-6 pb-5 pl-[6.5rem]">
                      <ul className="space-y-2">
                        {m.points.map((p, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm md:text-base text-[#4A5568]">
                            <i className="fas fa-check mt-1 shrink-0" style={{ color: BRAND.turq }} />
                            <span>{tx(p)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-10 text-center">
            <button
              onClick={() => openRegister(tiers.find((t) => t.label === "Standard") || tiers[0])}
              className="rounded-full px-8 py-4 font-bold text-white shadow-xl hover:-translate-y-0.5 transition-all inline-flex items-center justify-center gap-2"
              style={{ background: `linear-gradient(130deg, ${BRAND.accent} 0%, ${BRAND.deep} 100%)` }}
              data-testid="agenda-register"
            >
              {tx("Bli biletën")} <i className="fas fa-arrow-right" />
            </button>
          </div>
        </div>
      </section>

      {/* TICKETS */}
      <section id="biletat" className="max-w-7xl mx-auto px-6 py-20 md:py-28">
        <div className="text-center mb-14">
          <p className="text-xs font-black uppercase tracking-widest" style={{ color: BRAND.turq }}>{tx("Biletat")}</p>
          <h2 className="mt-3 text-3xl md:text-5xl font-black leading-tight" style={{ color: BRAND.deep }}>
            {tx("Zgjidh paketën tënde")}
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5 items-stretch">
          {tiers.map((t, idx) => {
            const isHighlight = (t.label || "").toLowerCase() === "vip";
            return (
              <div
                key={t.label}
                className={`relative rounded-3xl p-7 border-2 shadow-sm hover:shadow-xl transition-all flex flex-col ${isHighlight ? "border-[#1CA3B0] bg-gradient-to-br from-white to-[#E6F7F8] md:-my-6 md:py-12 md:shadow-xl ring-4 ring-[#1CA3B0]/15 z-10" : "border-slate-200 bg-white"}`}
              >
                {isHighlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-4 py-1 text-xs font-black uppercase tracking-wider text-white whitespace-nowrap shadow-lg" style={{ background: BRAND.turq }}>
                    {tx("⭐ Më e Zgjedhura")}
                  </div>
                )}
                <h3 className="text-xs font-black uppercase tracking-wider" style={{ color: BRAND.turq }}>{t.label}</h3>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className={`font-black ${isHighlight ? "text-6xl" : "text-5xl"}`} style={{ color: BRAND.deep }}>€{Number(t.price).toFixed(0)}</span>
                </div>
                <ul className="mt-6 space-y-2.5 flex-grow">
                  {(t.features || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#4A5568]">
                      <i className="fas fa-check mt-1 shrink-0" style={{ color: BRAND.turq }} />
                      <span>{tx(f)}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => openRegister(t)}
                  className={`mt-7 w-full rounded-full px-6 py-3 font-bold transition-all hover:-translate-y-0.5 ${isHighlight ? "text-white shadow-lg" : "border-2"}`}
                  style={isHighlight
                    ? { background: `linear-gradient(130deg, ${BRAND.accent} 0%, ${BRAND.deep} 100%)` }
                    : { borderColor: BRAND.deep, color: BRAND.deep }}
                  data-testid={`ticket-${t.label}`}
                >
                  {tx("Regjistrohu")}
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28" style={{ background: BRAND.cream }}>
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-xs font-black uppercase tracking-widest" style={{ color: BRAND.turq }}>{tx("Pyetje të Shpeshta")}</p>
            <h2 className="mt-3 text-3xl md:text-5xl font-black" style={{ color: BRAND.deep }}>FAQ</h2>
          </div>
          <div className="space-y-3">
            {faqItems.map((item, idx) => {
              const open = openFaqIdx === idx;
              return (
                <div key={idx} className="rounded-xl bg-white border border-slate-200 overflow-hidden">
                  <button
                    onClick={() => setOpenFaqIdx(open ? null : idx)}
                    className="w-full px-5 py-4 text-left flex items-center justify-between gap-4 hover:bg-slate-50 transition-colors"
                  >
                    <span className="font-bold text-base md:text-lg" style={{ color: BRAND.deep }}>{tx(item.q)}</span>
                    <i className={`fas fa-plus text-sm shrink-0 transition-transform ${open ? "rotate-45" : ""}`} style={{ color: BRAND.turq }} />
                  </button>
                  {open && (
                    <div className="px-5 pb-5 text-sm md:text-base text-[#4A5568] leading-relaxed">
                      {tx(item.a)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 md:py-28 text-white relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${BRAND.deep} 0%, ${BRAND.dark} 100%)` }}>
        <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
          backgroundImage: "radial-gradient(circle at 10% 10%, rgba(60,212,225,0.5) 0%, transparent 50%), radial-gradient(circle at 90% 90%, rgba(28,163,176,0.5) 0%, transparent 50%)",
        }} />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-black leading-tight text-white" style={{ textWrap: "balance" }}>
            {tx("Gati për të zgjidhur rastet")}<br/>{tx("që sot i referon?")}
          </h2>
          <p className="mt-5 text-lg max-w-2xl mx-auto" style={{ color: BRAND.textLight }}>
            {tx("27 Nëntor 2026 · Tiranë · Vende të kufizuara")}
          </p>
          <button
            onClick={() => openRegister(tiers.find((t) => t.label === "Standard") || tiers[0])}
            className="mt-10 rounded-full px-10 py-5 font-black text-lg text-white shadow-xl hover:-translate-y-1 transition-all inline-flex items-center justify-center gap-3"
            style={{ background: `linear-gradient(130deg, ${BRAND.accent} 0%, ${BRAND.deep} 100%)`, boxShadow: `0 25px 50px -10px ${BRAND.accent}66` }}
            data-testid="final-register"
          >
            {tx("Rezervo vendin tënd")} <i className="fas fa-arrow-right" />
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#0A2A47] text-white py-10 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={`${BACKEND_URL}/api/files/local/logo-clean.png`} alt="Albanian Aligners Academy" className="h-8 w-auto" />
            <span className="text-sm font-bold text-white">Albanian Aligners Academy</span>
          </div>
          <div className="flex items-center gap-5 text-sm text-white">
            <a href="tel:+355696056794" className="hover:text-[#3CD4E1] transition-colors text-white"><i className="fas fa-phone mr-2" />+355 696056794</a>
            <a href="https://aligners.academy" className="hover:text-[#3CD4E1] transition-colors text-white">aligners.academy</a>
          </div>
          <p className="text-xs text-white">© 2026 Albanian Aligners Academy</p>
        </div>
      </footer>

      {/* Registration popup (reuses the full checkout flow) */}
      {openPopup && event && (
        <PaidRegistrationPopup
          event={event}
          paymentMethods={paymentMethods}
          selectedTier={openPopup.tier || null}
          onClose={closePopup}
        />
      )}
    </div>
  );
};

export default BeyondSimpleAlignersLanding;
